import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import {
  CAMERA_CONNECTION_OUTCOME,
  CameraConnectionIssueReason,
  captureCameraEvent,
  EXAM_EVENTS,
  secondsSince,
} from 'src/services/posthogEvents';

import useRemoteBoundStore from 'src/store/useRemoteBoundStore';

import CameraConnection from 'src/models/Camera/CameraConnection';
import { I18NextNS } from 'src/models/Language.model';

import { useConnectionRetry } from 'src/hooks/useConnectionRetry';

import CameraConnectionIssueModal from './CameraConnectionIssueModal';
import CameraConnectionRetriedModal from './CameraConnectionRetriedModal';

interface CameraConnectionIssueControllerProps {
  show: boolean;
  onConnectionRestored: () => void;
  secondaryButton: React.ReactNode;
  showRetakeInstruction: boolean;
}

export default function CameraConnectionIssueController({
  show,
  onConnectionRestored,
  secondaryButton,
  showRetakeInstruction,
}: CameraConnectionIssueControllerProps) {
  const { cameraData, cameraConnectionIssueReason } = useRemoteBoundStore();
  const { t } = useTranslation(I18NextNS.COMMON);

  const reason =
    cameraConnectionIssueReason ?? CameraConnectionIssueReason.LOST_HEARTBEAT;

  const cameraIp = cameraData?.ip;

  const checkCameraConnection = useCallback(async (): Promise<boolean> => {
    if (!cameraIp) return false;
    try {
      await CameraConnection.loadCameraData(
        cameraIp,
        t('message.error.cameraIpMissingMsg'),
      );
      await CameraConnection.getInstance().getCurrentCaptureProcess();
      return true;
    } catch {
      return false;
    }
  }, [cameraIp, t]);

  const { reconnectionStatus, isCheckingConnection, handleCheckConnection } =
    useConnectionRetry(checkCameraConnection);

  const handleRetryClick = useCallback(async () => {
    const startedAt = performance.now();
    captureCameraEvent(EXAM_EVENTS.CAMERA_CONNECTION_RETRY_INITIATED, {
      reason,
    });
    const isConnected = await handleCheckConnection();
    if (isConnected === undefined) return; // a check was already in flight
    captureCameraEvent(EXAM_EVENTS.CAMERA_CONNECTION_RETRIED, {
      reason,
      outcome: isConnected
        ? CAMERA_CONNECTION_OUTCOME.SUCCESS
        : CAMERA_CONNECTION_OUTCOME.FAILURE,
      duration_seconds: secondsSince(startedAt),
    });
  }, [handleCheckConnection, reason]);

  if (reconnectionStatus) {
    return (
      <CameraConnectionRetriedModal
        show={show}
        onClose={onConnectionRestored}
        showRetakeInstruction={showRetakeInstruction}
      />
    );
  }

  return (
    <CameraConnectionIssueModal
      show={show}
      shouldShowFailedCard={reconnectionStatus === false}
      isCheckingConnection={isCheckingConnection}
      handleCheckConnection={handleRetryClick}
      secondaryButton={secondaryButton}
    />
  );
}
